(function () {
  'use strict';

  class CustomCollectionSort {
    constructor(productList) {
      this.productList = productList;

      this.collectionHandle =
        productList.dataset.customSortCollection || '';

      this.sectionId =
        productList.dataset.customSortSection || '';

      this.currentPage =
        parseInt(
          productList.dataset.customSortPage || '1',
          10
        );

      this.totalPages =
        parseInt(
          productList.dataset.customSortPages || '1',
          10
        );

      this.pageSize =
        parseInt(
          productList.dataset.customSortPageSize || '24',
          10
        );

      this.sortValue =
        new URL(window.location.href)
          .searchParams
          .get('sort_by') || '';

      this.pageCache = new Map();

      if (!this.isCustomSort()) {
        return;
      }

      this.init();
    }

    /*
     * ------------------------------------------------------------
     * Check Custom Sort
     * ------------------------------------------------------------
     */
    isCustomSort() {
      const nativeSortValues = new Set([
        'manual',
        'best-selling',
        'title-ascending',
        'title-descending',
        'price-ascending',
        'price-descending',
        'created-ascending',
        'created-descending'
      ]);

      const value =
        this.sortValue
          .trim()
          .toLowerCase();

      return value &&
        !nativeSortValues.has(value);
    }

    /*
     * ------------------------------------------------------------
     * Initialization
     * ------------------------------------------------------------
     */
    async init() {
      try {
        /*
         * --------------------------------------------------------
         * STEP 1
         *
         * First get only the Collection pages that are necessary
         * to calculate the current page.
         *
         * We DO NOT fetch the entire Collection anymore.
         * --------------------------------------------------------
         */

        const initialPages =
          this.getInitialPagesToFetch();

        const initialResults =
          await Promise.all(
            initialPages.map(
              page =>
                this.getCollectionPage(page)
            )
          );

        let allProducts =
          this.flattenPages(
            initialResults
          );

        if (!allProducts.length) {
          console.warn(
            '[Custom Collection Sort] No Collection products found.'
          );

          return;
        }

        /*
         * --------------------------------------------------------
         * STEP 2
         *
         * Build handle -> product map.
         * --------------------------------------------------------
         */
        let productMap =
          this.createProductMap(
            allProducts
          );

        /*
         * --------------------------------------------------------
         * STEP 3
         *
         * Resolve pinned handles.
         *
         * We keep the "-" separator.
         * --------------------------------------------------------
         */
        let pinnedHandles =
          this.resolveHandles(
            this.sortValue,
            productMap
          );

        /*
         * --------------------------------------------------------
         * If some pinned handles are not inside the currently
         * fetched pages, fetch additional Collection pages only
         * until those handles can be found.
         * --------------------------------------------------------
         */
        if (
          !this.allHandlesResolved(
            pinnedHandles,
            productMap
          )
        ) {
          const extraPages =
            await this.findMissingPinnedProducts(
              pinnedHandles,
              productMap
            );

          if (extraPages.length) {
            const extraResults =
              await Promise.all(
                extraPages.map(
                  page =>
                    this.getCollectionPage(page)
                )
              );

            allProducts =
              this.flattenPages(
                [
                  ...initialResults,
                  ...extraResults
                ]
              );

            productMap =
              this.createProductMap(
                allProducts
              );

            pinnedHandles =
              this.resolveHandles(
                this.sortValue,
                productMap
              );
          }
        }

        if (!pinnedHandles.length) {
          console.warn(
            '[Custom Collection Sort] No valid handles found:',
            this.sortValue
          );

          return;
        }

        this.pinnedHandles =
          pinnedHandles;

        /*
         * --------------------------------------------------------
         * STEP 4
         *
         * Calculate the final page handles.
         * --------------------------------------------------------
         */
        const desiredHandles =
          this.buildDesiredHandles(
            allProducts,
            pinnedHandles
          );

        if (!desiredHandles.length) {
          return;
        }

        /*
         * --------------------------------------------------------
         * STEP 5
         *
         * Determine which original Collection pages contain
         * the final products.
         *
         * Only these pages need their actual HTML.
         * --------------------------------------------------------
         */
        const requiredPages =
          this.findPagesForHandles(
            allProducts,
            desiredHandles
          );

        /*
         * Current page is always required.
         */
        requiredPages.add(
          this.currentPage
        );

        /*
         * Fetch only required pages that haven't already
         * been fetched.
         */
        const pagesToFetch =
          Array.from(
            requiredPages
          ).filter(
            page =>
              !this.pageCache.has(page)
          );

        if (pagesToFetch.length) {
          await Promise.all(
            pagesToFetch.map(
              page =>
                this.getCollectionPage(
                  page
                )
            )
          );
        }

        /*
         * --------------------------------------------------------
         * STEP 6
         *
         * Build handle -> actual DOM element map.
         * --------------------------------------------------------
         */
        const productCardMap =
          new Map();

        requiredPages.forEach(
          page => {
            const products =
              this.pageCache.get(
                page
              ) || [];

            products.forEach(
              product => {
                if (
                  product.handle &&
                  !productCardMap.has(
                    product.handle
                  )
                ) {
                  productCardMap.set(
                    product.handle,
                    product
                  );
                }
              }
            );
          }
        );

        /*
         * --------------------------------------------------------
         * STEP 7
         *
         * Get actual product cards in final order.
         * --------------------------------------------------------
         */
        const desiredProducts =
          desiredHandles
            .map(
              handle =>
                productCardMap.get(
                  handle
                )
            )
            .filter(Boolean);

        if (!desiredProducts.length) {
          return;
        }

        /*
         * --------------------------------------------------------
         * STEP 8
         *
         * Replace ONLY product cards.
         *
         * Promotion Image / Promotion Video
         * remain untouched.
         * --------------------------------------------------------
         */
        this.applyDesiredPage(
          desiredProducts
        );

      } catch (error) {
        console.error(
          '[Custom Collection Sort]',
          error
        );
      }
    }

    /*
     * ------------------------------------------------------------
     * Determine initial pages
     * ------------------------------------------------------------
     *
     * We only need enough original products to calculate
     * the requested page.
     *
     * Example:
     *
     * Page 1
     * 24 products
     * 3 pinned products
     *
     * We need roughly:
     *
     * 24 + 3 = 27 products
     *
     * Therefore only 2 Collection pages need to be checked.
     * ------------------------------------------------------------
     */
    getInitialPagesToFetch() {
      const pinnedEstimate =
        this.estimatePinnedCount();

      const requiredProducts =
        (
          this.currentPage *
          this.pageSize
        ) +
        pinnedEstimate;

      const requiredPages =
        Math.ceil(
          requiredProducts /
          this.pageSize
        );

      const pageCount =
        Math.min(
          Math.max(
            requiredPages,
            this.currentPage
          ),
          this.totalPages
        );

      return Array.from(
        {
          length:
            pageCount
        },
        (_, index) =>
          index + 1
      );
    }

    /*
     * ------------------------------------------------------------
     * Estimate number of pinned products.
     *
     * Because "-" is also part of product handles, we cannot
     * simply split the URL by "-".
     *
     * This estimate is intentionally conservative.
     *
     * For a normal Custom Sort URL, 10 pinned products is already
     * more than enough for the initial calculation.
     * ------------------------------------------------------------
     */
    estimatePinnedCount() {
      if (!this.sortValue) {
        return 1;
      }

      /*
       * Count likely product segments.
       *
       * We intentionally cap this value to avoid requesting
       * the entire Collection just because the URL is long.
       */
      const hyphenCount =
        (
          this.sortValue.match(
            /-/g
          ) || []
        ).length;

      return Math.min(
        Math.max(
          hyphenCount + 1,
          1
        ),
        10
      );
    }

    /*
     * ------------------------------------------------------------
     * Flatten Collection pages
     * ------------------------------------------------------------
     */
    flattenPages(
      pages
    ) {
      const result = [];

      pages.forEach(
        page => {
          if (
            Array.isArray(page)
          ) {
            result.push(
              ...page
            );
          }
        }
      );

      /*
       * Remove duplicate handles while preserving order.
       */
      const map =
        new Map();

      result.forEach(
        product => {
          if (
            product.handle &&
            !map.has(
              product.handle
            )
          ) {
            map.set(
              product.handle,
              product
            );
          }
        }
      );

      return Array.from(
        map.values()
      );
    }

    /*
     * ------------------------------------------------------------
     * Create product map
     * ------------------------------------------------------------
     */
    createProductMap(
      products
    ) {
      const map =
        new Map();

      products.forEach(
        product => {
          if (
            product.handle &&
            !map.has(
              product.handle
            )
          ) {
            map.set(
              product.handle,
              product
            );
          }
        }
      );

      return map;
    }

    /*
     * ------------------------------------------------------------
     * Check whether all pinned handles are resolved
     * ------------------------------------------------------------
     */
    allHandlesResolved(
      pinnedHandles,
      productMap
    ) {
      if (
        !pinnedHandles.length
      ) {
        return false;
      }

      return pinnedHandles.every(
        handle =>
          productMap.has(
            handle
          )
      );
    }

    /*
     * ------------------------------------------------------------
     * Find missing pinned products
     * ------------------------------------------------------------
     *
     * We progressively fetch additional Collection pages.
     *
     * IMPORTANT:
     *
     * We stop as soon as all requested handles are found.
     *
     * We no longer automatically fetch every Collection page.
     * ------------------------------------------------------------
     */
    async findMissingPinnedProducts(
      pinnedHandles,
      productMap
    ) {
      const missingHandles =
        pinnedHandles.filter(
          handle =>
            !productMap.has(
              handle
            )
        );

      if (
        !missingHandles.length
      ) {
        return [];
      }

      const pages =
        [];

      /*
       * Start from the first page that has not already
       * been loaded.
       */
      let page =
        1;

      while (
        page <=
        this.totalPages
      ) {
        if (
          !this.pageCache.has(
            page
          )
        ) {
          pages.push(
            page
          );
        }

        /*
         * If enough pages have been selected,
         * fetch them in batches.
         */
        if (
          pages.length >= 3
        ) {
          break;
        }

        page++;
      }

      return pages;
    }

    /*
     * ------------------------------------------------------------
     * Resolve handles
     * ------------------------------------------------------------
     */
    resolveHandles(
      sortValue,
      productMap
    ) {
      const value =
        String(
          sortValue || ''
        )
          .trim()
          .toLowerCase();

      if (!value) {
        return [];
      }

      const handles =
        Array.from(
          productMap.keys()
        ).sort(
          (a, b) =>
            b.length -
            a.length
        );

      const result =
        [];

      let remaining =
        value;

      while (
        remaining
      ) {
        const handle =
          handles.find(
            currentHandle =>
              remaining ===
                currentHandle ||
              remaining.startsWith(
                currentHandle +
                  '-'
              )
          );

        if (!handle) {
          return [];
        }

        result.push(
          handle
        );

        if (
          remaining ===
          handle
        ) {
          remaining =
            '';
        } else {
          remaining =
            remaining.slice(
              handle.length +
                1
            );
        }
      }

      return result;
    }

    /*
     * ------------------------------------------------------------
     * Build final product handles
     * ------------------------------------------------------------
     */
    buildDesiredHandles(
      allProducts,
      pinnedHandles
    ) {
      const pinnedSet =
        new Set(
          pinnedHandles
        );

      /*
       * Remove pinned products from their original positions.
       */
      const normalHandles =
        allProducts
          .map(
            product =>
              product.handle
          )
          .filter(
            handle =>
              handle &&
              !pinnedSet.has(
                handle
              )
          );

      /*
       * Final global order:
       *
       * pinned
       * +
       * normal Collection products
       */
      const globalHandles = [
        ...pinnedHandles,
        ...normalHandles
      ];

      /*
       * Current Shopify page.
       */
      const startIndex =
        (
          this.currentPage -
          1
        ) *
        this.pageSize;

      const endIndex =
        startIndex +
        this.pageSize;

      return globalHandles.slice(
        startIndex,
        endIndex
      );
    }

    /*
     * ------------------------------------------------------------
     * Find original Collection pages for final handles
     * ------------------------------------------------------------
     */
    findPagesForHandles(
      allProducts,
      desiredHandles
    ) {
      const pages =
        new Set();

      const handleSet =
        new Set(
          desiredHandles
        );

      allProducts.forEach(
        (product, index) => {
          if (
            handleSet.has(
              product.handle
            )
          ) {
            pages.add(
              Math.floor(
                index /
                  this.pageSize
              ) + 1
            );
          }
        }
      );

      return pages;
    }

    /*
     * ------------------------------------------------------------
     * Fetch Collection section page
     * ------------------------------------------------------------
     */
    async getCollectionPage(
      page
    ) {
      if (
        this.pageCache.has(page)
      ) {
        return this.pageCache.get(
          page
        );
      }

      const url =
        new URL(
          window.location.href
        );

      /*
       * Remove Custom Sort.
       */
      url.searchParams.delete(
        'sort_by'
      );

      /*
       * Request Collection page.
       */
      url.searchParams.set(
        'page',
        String(page)
      );

      /*
       * Request only the current section.
       */
      url.searchParams.set(
        'sections',
        this.sectionId
      );

      try {
        const response =
          await fetch(
            url.toString(),
            {
              headers: {
                'X-Requested-With':
                  'XMLHttpRequest'
              }
            }
          );

        if (!response.ok) {
          console.error(
            '[Custom Collection Sort] HTTP:',
            response.status
          );

          return [];
        }

        const data =
          await response.json();

        const sectionHtml =
          data[
            this.sectionId
          ];

        if (!sectionHtml) {
          console.error(
            '[Custom Collection Sort] Section not found:',
            this.sectionId
          );

          return [];
        }

        const temporary =
          document.createElement(
            'div'
          );

        temporary.innerHTML =
          sectionHtml;

        const products =
          Array.from(
            temporary.querySelectorAll(
              '.custom-sort-product'
            )
          ).map(
            element => ({
              handle:
                (
                  element.dataset
                    .productHandle ||
                  ''
                )
                  .trim()
                  .toLowerCase(),

              element
            })
          );

        this.pageCache.set(
          page,
          products
        );

        return products;

      } catch (error) {
        console.error(
          '[Custom Collection Sort] Fetch failed:',
          error
        );

        return [];
      }
    }

    /*
     * ------------------------------------------------------------
     * Apply final page
     * ------------------------------------------------------------
     *
     * ONLY .custom-sort-product is replaced.
     *
     * Promotion Image / Promotion Video
     * are never selected.
     * ------------------------------------------------------------
     */
    applyDesiredPage(
      products
    ) {
      const existing =
        Array.from(
          this.productList.querySelectorAll(
            '.custom-sort-product'
          )
        );

      if (
        !existing.length
      ) {
        return;
      }

      const count =
        Math.min(
          existing.length,
          products.length
        );

      for (
        let i = 0;
        i < count;
        i++
      ) {
        const oldElement =
          existing[i];

        const newElement =
          products[i].element;

        if (
          oldElement ===
          newElement
        ) {
          continue;
        }

        if (
          oldElement.parentNode
        ) {
          oldElement.parentNode.replaceChild(
            newElement,
            oldElement
          );
        }
      }
    }
  }

  /*
   * ------------------------------------------------------------
   * Initialization
   * ------------------------------------------------------------
   */
  function initCustomCollectionSort(
    root
  ) {
    if (
      !root ||
      !root.querySelectorAll
    ) {
      return;
    }

    const productLists =
      root.querySelectorAll(
        'product-list[data-custom-sort-collection]'
      );

    productLists.forEach(
      productList => {
        if (
          productList.dataset
            .customSortInitialized ===
          'true'
        ) {
          return;
        }

        productList.dataset
          .customSortInitialized =
          'true';

        new CustomCollectionSort(
          productList
        );
      }
    );
  }

  /*
   * ------------------------------------------------------------
   * Initial load
   * ------------------------------------------------------------
   */
  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initCustomCollectionSort(
          document
        );
      }
    );
  } else {
    initCustomCollectionSort(
      document
    );
  }

  /*
   * ------------------------------------------------------------
   * Shopify section reload
   * ------------------------------------------------------------
   */
  document.addEventListener(
    'shopify:section:load',
    event => {
      initCustomCollectionSort(
        event.target
      );
    }
  );
})();